import { Play, Pause, Pencil, Trash2, Zap } from "lucide-react";
import { useState } from "react";

import type { Automation } from "@/lib/types";

import {
  useDeleteAutomation,
  useTriggerAutomation,
  usePauseAutomation,
  useResumeAutomation,
} from "@/hooks/useAutomations";
import { useStore } from "@/lib/store";

import { AutomationModal } from "./AutomationModal";
import { describeSchedule, relativeTime } from "./utils";

interface AutomationCardProps {
  automation: Automation;
}

export function AutomationCard({ automation }: AutomationCardProps): JSX.Element {
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const selectAutomation = useStore((state) => state.selectAutomation);
  const showToast = useStore((state) => state.showToast);

  const deleteAutomation = useDeleteAutomation();
  const triggerAutomation = useTriggerAutomation();
  const pauseAutomation = usePauseAutomation();
  const resumeAutomation = useResumeAutomation();

  const isEnabled = automation.status === "enabled";

  async function handleToggle() {
    try {
      if (isEnabled) {
        await pauseAutomation.mutateAsync(automation.id);
        showToast("Automation paused", "success");
      } else {
        await resumeAutomation.mutateAsync(automation.id);
        showToast("Automation resumed", "success");
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed", "error");
    }
  }

  async function handleTrigger() {
    try {
      await triggerAutomation.mutateAsync(automation.id);
      showToast("Automation triggered", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to trigger", "error");
    }
  }

  async function handleDelete() {
    try {
      await deleteAutomation.mutateAsync(automation.id);
      showToast("Automation deleted", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to delete", "error");
    }
    setShowDeleteConfirm(false);
  }

  return (
    <>
      <button
        type="button"
        className="group w-full cursor-pointer rounded-lg border border-cyber-border px-4 py-3 text-left transition-all hover:border-cyber-cyan/30"
        onClick={() => selectAutomation(automation.id)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-cyber-text">
                {automation.name}
              </span>
              <span
                className={`rounded border px-1.5 py-0.5 text-[10px] ${
                  isEnabled
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                    : "border-cyber-border bg-cyber-muted/10 text-cyber-muted"
                }`}
              >
                {isEnabled ? "Active" : "Paused"}
              </span>
            </div>
            <div className="mt-1 text-xs text-cyber-muted">
              {describeSchedule(automation.schedule)}
            </div>
            <div className="mt-1.5 flex items-center gap-3 text-[11px] text-cyber-muted/70">
              {automation.lastRunAt && <span>Last: {relativeTime(automation.lastRunAt)}</span>}
              {automation.nextRunAt && <span>Next: {relativeTime(automation.nextRunAt)}</span>}
              {automation.modelId && (
                <span className="text-cyber-muted/60" title={automation.modelId}>
                  {automation.modelId.split("/").pop()}
                </span>
              )}
              {automation.target.source === "slack" && (
                <span className="text-cyber-cyan/60">Slack</span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div
            role="toolbar"
            className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleTrigger}
              disabled={triggerAutomation.isPending}
              className="rounded p-1.5 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan"
              title="Run now"
            >
              <Zap size={14} />
            </button>
            <button
              type="button"
              onClick={handleToggle}
              disabled={pauseAutomation.isPending || resumeAutomation.isPending}
              className="rounded p-1.5 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan"
              title={isEnabled ? "Pause" : "Resume"}
            >
              {isEnabled ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <button
              type="button"
              onClick={() => setShowEdit(true)}
              className="rounded p-1.5 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan"
              title="Edit"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded p-1.5 text-cyber-muted transition-all hover:bg-cyber-red/10 hover:text-cyber-red"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </button>

      {showEdit && <AutomationModal automation={automation} onClose={() => setShowEdit(false)} />}

      {showDeleteConfirm && (
        <>
          <div
            role="presentation"
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
            <div className="pointer-events-auto w-72 rounded-lg border border-cyber-border bg-cyber-surface p-4">
              <p className="mb-4 text-sm text-cyber-text">
                Delete &ldquo;{automation.name}&rdquo;?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1.5 text-sm text-cyber-muted transition-all hover:text-cyber-text"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteAutomation.isPending}
                  className="rounded border border-cyber-red/30 bg-cyber-red/10 px-3 py-1.5 text-sm text-cyber-red transition-all hover:bg-cyber-red/20 disabled:opacity-50"
                >
                  {deleteAutomation.isPending ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
